import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { unarchiveAgent } from "@/services/templateService";
import { voiceAgentService } from "@/services/voiceAgentService";

export const useUnarchiveAgent = () => {
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [planLimitModalOpen, setPlanLimitModalOpen] = useState(false);
  const { organization } = useAuth();
  const { toast } = useToast();

  const unarchive = async (
    agentId: string,
    agentType: "chat" | "voice" = "chat",
  ) => {
    if (isUnarchiving) return false;

    if (!organization?.id) {
      toast({
        variant: "destructive",
        title: "Organization required",
        description: "No organization found for unarchiving agent",
      });
      return false;
    }

    setIsUnarchiving(true);
    try {
      let error: any = null;

      if (agentType === "voice") {
        const { success, error: voiceError } =
          await voiceAgentService.unarchiveVoiceAgent(agentId);
        if (!success) {
          error = voiceError;
        }
      } else {
        const { error: chatError } = await unarchiveAgent(
          agentId,
          organization.id,
        );
        error = chatError;
      }

      if (error === "plan_limit_reached") {
        setPlanLimitModalOpen(true);
        return "plan_limit_reached";
      }

      if (error) {
        throw error;
      }

      toast({
        title: "Agent unarchived",
        description: `The ${agentType} agent has been unarchived successfully`,
      });

      return true;
    } catch (error: any) {
      console.error("Error unarchiving agent:", error);
      toast({
        variant: "destructive",
        title: "Failed to unarchive agent",
        description: error.message || "An unexpected error occurred",
      });
      return false;
    } finally {
      setIsUnarchiving(false);
    }
  };

  return {
    unarchive,
    isUnarchiving,
    planLimitModalOpen,
    setPlanLimitModalOpen,
  };
};
